import { v4 as uuidv4 } from 'uuid'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { logoutCleanup } from '../../utils/logoutCleanup'
import { mockAxiosInstance } from './mockAxiosInstance'
import axios, {
    AxiosInstance,
    InternalAxiosRequestConfig,
    type AxiosError,
} from 'axios'
import { Session } from './types'
import { router } from '@/router'
import { debugLog as log } from '@/utils/debugLog'
import { setupMousemoveListener } from './sessionRefresh'

// Constants
const SESSION_REFRESH_INTERVAL = 60 * 1000

// Singleton Axios instance
const axiosInstance: AxiosInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL as string,
    responseType: 'json',
    timeout: 30000,
})

// --- Logging and Auth Header ---
const setAuthorizationHeader = (token: string) => {
    if (axiosInstance.defaults.headers['Authorization'] !== `Bearer ${token}`) {
        log('[useAxiosInstance] setAuthorizationHeader', token)
    }
    axiosInstance.defaults.headers['Authorization'] = `Bearer ${token}`
}

// --- Logout ---
const logout = () => {
    log('[useAxiosInstance] Logging out...')
    logoutCleanup()
    void router.navigate({ to: '/login' })
}

// --- Session Refresh ---
const refreshSession = async (): Promise<Session> => {
    const { refreshToken, sessionId, setSession } = useAuthStore.getState()
    log('[useAxiosInstance] refreshSession called', { sessionId, refreshToken })
    const response = await axiosInstance.put<Session>(
        `/api/v1/sessions/${sessionId}`,
        { refreshToken }
    )
    setSession(response.data)
    setAuthorizationHeader(response.data.accessToken)
    log('[useAxiosInstance] refreshSession success', response.data)
    return response.data
}

// --- Axios Interceptors ---
axiosInstance.interceptors.request.use(
    (config) => {
        config.headers['X-Trace-Id'] = uuidv4()
        if (config.responseType !== 'blob') {
            config.responseType = 'json'
        }
        // updateActivityTimestamp is called inside sessionRefreshOnActivity
        log('[useAxiosInstance] Request:', config.method, config.url)
        return config
    },
    (error: Error) => {
        log('[useAxiosInstance] Request error:', error)
        return Promise.reject(error)
    }
)

let isRefreshing = false
let failedRequestQueue: Array<{
    resolve: (value?: unknown) => void
    reject: (reason?: unknown) => void
}> = []

const handleResponseError = async (error: unknown) => {
    log('[useAxiosInstance] handleResponseError', {
        error,
        isRefreshing,
        failedRequestQueueLength: failedRequestQueue.length,
    })
    const axiosError = error as AxiosError<{ message?: string } | undefined>
    const sessionId = useAuthStore.getState().sessionId

    if (!axiosError.config) {
        log('[useAxiosInstance] Warning: error without config, not logging out')
        return Promise.reject(axiosError)
    }

    const originalRequest = axiosError.config as AxiosError['config'] & {
        _retried?: boolean
    }

    if (
        axiosError.config.url?.includes(`/api/v1/sessions/${sessionId}`) &&
        axiosError.config.method === 'put'
    ) {
        log('[useAxiosInstance] Error on session refresh endpoint, logging out')
        logout()
        return Promise.resolve()
    }

    if (originalRequest._retried) {
        log(
            '[useAxiosInstance] Request already retried, rejecting',
            originalRequest.url
        )
        return Promise.reject(axiosError)
    }

    if (axiosError.response?.status === 401) {
        log('[useAxiosInstance] 401 error', axiosError.response.data)
        const message = axiosError.response.data?.message
        if (message !== 'EXPIRED_TOKEN') {
            log('[useAxiosInstance] 401 not EXPIRED_TOKEN, logging out')
            logout()
            return Promise.resolve()
        }

        originalRequest._retried = true
        log('[useAxiosInstance] Marked request as retried', originalRequest.url)

        const retryPromise = new Promise((resolve, reject) => {
            failedRequestQueue.push({ resolve, reject })
            log(
                '[useAxiosInstance] Added to failedRequestQueue',
                failedRequestQueue.length
            )
        }).then(() => {
            const retriedRequest: InternalAxiosRequestConfig = {
                ...originalRequest,
                responseType: 'json',
                transformRequest: axios.defaults.transformRequest,
                transformResponse: axios.defaults.transformResponse,
            }
            retriedRequest.headers['Authorization'] =
                `Bearer ${useAuthStore.getState().accessToken}`

            log(
                '[useAxiosInstance] Retrying original request',
                retriedRequest.url
            )
            return axiosInstance.request(retriedRequest)
        })

        if (!isRefreshing) {
            isRefreshing = true
            log('[useAxiosInstance] Refreshing session...')
            refreshSessionAndUpdateFailedQueue()
        } else {
            log('[useAxiosInstance] Already refreshing session, waiting...')
        }

        return retryPromise
    }

    log('[useAxiosInstance] Non-401 error, rejecting')
    return Promise.reject(axiosError)
}

const refreshSessionAndUpdateFailedQueue = () => {
    refreshSession()
        .then(() => {
            log('[useAxiosInstance] Session refreshed, resolving failed queue')
            failedRequestQueue.forEach((promise) => {
                promise.resolve()
            })
        })
        .catch((error: unknown) => {
            log(
                '[useAxiosInstance] Session refresh failed, rejecting failed queue',
                error
            )
            failedRequestQueue.forEach((promise) => {
                promise.reject(error)
            })
            logout()
        })
        .finally(() => {
            isRefreshing = false
            failedRequestQueue = []
            log(
                '[useAxiosInstance] Reset isRefreshing and cleared failedRequestQueue'
            )
        })
}

axiosInstance.interceptors.response.use((response) => {
    log('[useAxiosInstance] Response:', response.config.url, response.status)
    return response
}, handleResponseError)

// --- Mousemove Listener & Mock Setup ---
setupMousemoveListener(SESSION_REFRESH_INTERVAL, axiosInstance)
mockAxiosInstance(axiosInstance)

// --- Exported API ---
export const useAxiosInstance = (token: string) => {
    setAuthorizationHeader(token)
    return axiosInstance
}
